/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useState, useEffect, useCallback } from 'react';
import { uploadManager, UploadTask, UploadConfig } from '../services/uploadManager';
import { useQueryClient } from '@tanstack/react-query';

export const useUploadManager = () => {
  const queryClient = useQueryClient();
  const [tasks, setTasks] = useState<UploadTask[]>([]);
  const [config, setConfig] = useState<UploadConfig>(uploadManager.getConfig());

  useEffect(() => {
    // Initial load
    setTasks(uploadManager.getTasks());
    setConfig(uploadManager.getConfig());

    // Subscribe to events
    const unsubProgress = uploadManager.on('progress', () => {
      setTasks([...uploadManager.getTasks()]);
    });

    const unsubComplete = uploadManager.on('complete', () => {
      setTasks([...uploadManager.getTasks()]);
      // Invalidate queries to refresh bucket list and objects
      queryClient.invalidateQueries({ queryKey: ['buckets'] });
      queryClient.invalidateQueries({ queryKey: ['objects'] });
    });

    const unsubError = uploadManager.on('error', () => {
      setTasks([...uploadManager.getTasks()]);
    });

    const unsubStatus = uploadManager.on('statusChange', () => {
      setTasks([...uploadManager.getTasks()]);
    });

    return () => {
      unsubProgress();
      unsubComplete();
      unsubError();
      unsubStatus();
    };
  }, [queryClient]);

  const uploadFile = useCallback(
    async (file: File, bucketId: string, projectId: string, path: string) => {
      return uploadManager.addUpload(file, bucketId, projectId, path);
    },
    []
  );

  const uploadFiles = useCallback(
    async (
      files: { file: File; path: string }[],
      bucketId: string,
      projectId: string
    ) => {
      return uploadManager.addUploads(files, bucketId, projectId);
    },
    []
  );

  const cancelUpload = useCallback((taskId: string) => {
    uploadManager.cancelUpload(taskId);
  }, []);

  const retryUpload = useCallback((taskId: string) => {
    uploadManager.retryUpload(taskId);
  }, []);

  const removeTask = useCallback((taskId: string) => {
    uploadManager.removeTask(taskId);
    setTasks([...uploadManager.getTasks()]);
  }, []);

  const clearCompleted = useCallback(() => {
    uploadManager.clearCompleted();
    setTasks([...uploadManager.getTasks()]);
  }, []);

  return {
    tasks,
    uploadFile,
    uploadFiles,
    cancelUpload,
    retryUpload,
    removeTask,
    clearCompleted,
    hasActiveUploads: uploadManager.hasActiveUploads(),
    config: {
      speedTier: config.speedTier,
      chunkSizeMB: config.chunkSize / (1024 * 1024),
      concurrency: config.concurrency,
    },
  };
};
